import { useState, useCallback } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { apiService } from '@/services/api';
import { UserRole } from '@/types/user';

interface ApiResponse<T = any> {
  data: T | null;
  loading: boolean;
  error: string | null;
}

interface UseRoleBasedApiReturn {
  // Generic request methods
  get: <T = any>(endpoint: string, options?: any) => Promise<T>;
  post: <T = any>(endpoint: string, data?: any, options?: any) => Promise<T>;
  put: <T = any>(endpoint: string, data?: any, options?: any) => Promise<T>;
  delete: <T = any>(endpoint: string, options?: any) => Promise<T>;
  patch: <T = any>(endpoint: string, data?: any, options?: any) => Promise<T>;
  
  // Admin-specific methods
  getAdminUsers: (params?: any) => Promise<any>;
  getAdminOperators: (params?: any) => Promise<any>;
  getAdminLocations: (params?: any) => Promise<any>;
  getAdminServices: (params?: any) => Promise<any>;
  getAdminBookings: (params?: any) => Promise<any>;
  
  // Operator-specific methods
  getOperatorLocations: (params?: any) => Promise<any>;
  getOperatorServices: (params?: any) => Promise<any>;
  getOperatorBookings: (params?: any) => Promise<any>;
  getOperatorDashboardData: () => Promise<any>;
  getOperatorProfile: () => Promise<any>;
  updateOperatorProfile: (data: any) => Promise<any>;
  
  // User-specific methods
  getUserBookings: (params?: any) => Promise<any>;
  getUserProfile: () => Promise<any>;
  updateUserProfile: (data: any) => Promise<any>;
  updateUserPassword: (data: any) => Promise<any>;
  updateUserAvatar: (data: FormData) => Promise<any>;
  getUserPreferences: () => Promise<any>;
  updateUserPreferences: (data: any) => Promise<any>;
  
  // Public methods
  getBlogs: (params?: any) => Promise<any>;
  submitContactForm: (data: any) => Promise<any>;
  
  // State management
  response: ApiResponse;
  resetResponse: () => void;
}

export function useRoleBasedApi(): UseRoleBasedApiReturn {
  const { user } = useAuth();
  const [response, setResponse] = useState<ApiResponse>({
    data: null,
    loading: false,
    error: null,
  });

  const resetResponse = useCallback(() => {
    setResponse({ data: null, loading: false, error: null });
  }, []);

  const makeRequest = useCallback(async <T = any>(
    requestFn: () => Promise<T>,
    options?: { showLoading?: boolean }
  ): Promise<T> => {
    const showLoading = options?.showLoading !== false;
    
    if (showLoading) {
      setResponse(prev => ({ ...prev, loading: true, error: null }));
    }

    try {
      const data = await requestFn();
      setResponse({ data, loading: false, error: null });
      return data;
    } catch (error: any) {
      const errorMessage = error.message || 'An error occurred';
      setResponse({ data: null, loading: false, error: errorMessage });
      throw error;
    }
  }, []);

  // Generic request methods
  const get = useCallback(<T = any>(endpoint: string, options?: any) => {
    if (!user) throw new Error('User not authenticated');
    return makeRequest(() => apiService.request('GET', endpoint, undefined, { ...options, user }));
  }, [makeRequest, user]);

  const post = useCallback(<T = any>(endpoint: string, data?: any, options?: any) => {
    if (!user) throw new Error('User not authenticated');
    return makeRequest(() => apiService.request('POST', endpoint, data, { ...options, user }));
  }, [makeRequest, user]);

  const put = useCallback(<T = any>(endpoint: string, data?: any, options?: any) => {
    if (!user) throw new Error('User not authenticated');
    return makeRequest(() => apiService.request('PUT', endpoint, data, { ...options, user }));
  }, [makeRequest, user]);

  const deleteRequest = useCallback(<T = any>(endpoint: string, options?: any) => {
    if (!user) throw new Error('User not authenticated');
    return makeRequest(() => apiService.request('DELETE', endpoint, undefined, { ...options, user }));
  }, [makeRequest, user]);

  const patch = useCallback(<T = any>(endpoint: string, data?: any, options?: any) => {
    if (!user) throw new Error('User not authenticated');
    return makeRequest(() => apiService.request('PATCH', endpoint, data, { ...options, user }));
  }, [makeRequest, user]);

  // Public request method for endpoints that don't require authentication
  const publicRequest = useCallback(<T = any>(
    method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH',
    endpoint: string,
    data?: any
  ) => {
    return makeRequest(() => apiService.request(method, endpoint, data, { isPublic: true }));
  }, [makeRequest]);

  // Admin-specific methods
  const getAdminUsers = useCallback((params?: any) => {
    if (!user) throw new Error('User not authenticated');
    return makeRequest(() => apiService.request('GET', '/users', params, { user }));
  }, [makeRequest, user]);

  const getAdminOperators = useCallback((params?: any) => {
    if (!user) throw new Error('User not authenticated');
    return makeRequest(() => apiService.request('GET', '/operators', params, { user }));
  }, [makeRequest, user]);

  const getAdminLocations = useCallback((params?: any) => {
    if (!user) throw new Error('User not authenticated');
    return makeRequest(() => apiService.request('GET', '/locations', params, { user }));
  }, [makeRequest, user]);

  const getAdminServices = useCallback((params?: any) => {
    if (!user) throw new Error('User not authenticated');
    return makeRequest(() => apiService.request('GET', '/services', params, { user }));
  }, [makeRequest, user]);

  const getAdminBookings = useCallback((params?: any) => {
    if (!user) throw new Error('User not authenticated');
    return makeRequest(() => apiService.request('GET', '/bookings', params, { user }));
  }, [makeRequest, user]);

  // Operator-specific methods
  const getOperatorLocations = useCallback((params?: any) => {
    if (!user) throw new Error('User not authenticated');
    return makeRequest(() => apiService.request('GET', '/locations', params, { user }));
  }, [makeRequest, user]);

  const getOperatorServices = useCallback((params?: any) => {
    if (!user) throw new Error('User not authenticated');
    return makeRequest(() => apiService.request('GET', '/services', params, { user }));
  }, [makeRequest, user]);

  const getOperatorBookings = useCallback((params?: any) => {
    if (!user) throw new Error('User not authenticated');
    return makeRequest(() => apiService.request('GET', '/bookings', params, { user }));
  }, [makeRequest, user]);

  const getOperatorDashboardData = useCallback(() => {
    if (!user) throw new Error('User not authenticated');
    return makeRequest(() => apiService.request('GET', '/dashboard', undefined, { user }));
  }, [makeRequest, user]);

  const getOperatorProfile = useCallback(() => {
    if (!user) throw new Error('User not authenticated');
    return makeRequest(() => apiService.request('GET', '/profile', undefined, { user }));
  }, [makeRequest, user]);

  const updateOperatorProfile = useCallback((data: any) => {
    if (!user) throw new Error('User not authenticated');
    return makeRequest(() => apiService.request('PUT', '/profile', data, { user }));
  }, [makeRequest, user]);

  // User-specific methods
  const getUserBookings = useCallback((params?: any) => {
    if (!user) throw new Error('User not authenticated');
    return makeRequest(() => apiService.request('GET', '/bookings', params, { user }));
  }, [makeRequest, user]);

  const getUserProfile = useCallback(() => {
    return publicRequest('GET', '/auth/user');
  }, [publicRequest]);

  const updateUserProfile = useCallback((data: any) => {
    if (!user) throw new Error('User not authenticated');
    return makeRequest(() => apiService.request('PUT', '/profile', data, { user }));
  }, [makeRequest, user]);

  const updateUserPassword = useCallback((data: any) => {
    if (!user) throw new Error('User not authenticated');
    return makeRequest(() => apiService.request('PUT', '/profile/password', data, { user }));
  }, [makeRequest, user]);

  const updateUserAvatar = useCallback((data: FormData) => {
    if (!user) throw new Error('User not authenticated');
    return makeRequest(() => apiService.request('POST', '/profile/avatar', data, { user }));
  }, [makeRequest, user]);

  const getUserPreferences = useCallback(() => {
    if (!user) throw new Error('User not authenticated');
    return makeRequest(() => apiService.request('GET', '/profile/preferences', undefined, { user }));
  }, [makeRequest, user]);

  const updateUserPreferences = useCallback((data: any) => {
    if (!user) throw new Error('User not authenticated');
    return makeRequest(() => apiService.request('POST', '/profile/preferences', data, { user }));
  }, [makeRequest, user]);

  // Public methods
  const getBlogs = useCallback((params?: any) => {
    return publicRequest('GET', '/blogs', params);
  }, [publicRequest]);

  const submitContactForm = useCallback((data: any) => {
    return publicRequest('POST', '/contact', data);
  }, [publicRequest]);

  return {
    // Generic methods
    get,
    post,
    put,
    delete: deleteRequest,
    patch,
    
    // Admin methods
    getAdminUsers,
    getAdminOperators,
    getAdminLocations,
    getAdminServices,
    getAdminBookings,
    
    // Operator methods
    getOperatorLocations,
    getOperatorServices,
    getOperatorBookings,
    getOperatorDashboardData,
    getOperatorProfile,
    updateOperatorProfile,
    
    // User methods
    getUserBookings,
    getUserProfile,
    updateUserProfile,
    updateUserPassword,
    updateUserAvatar,
    getUserPreferences,
    updateUserPreferences,
    
    // Public methods
    getBlogs,
    submitContactForm,
    
    // State
    response,
    resetResponse,
  };
}

// Specialized hooks for specific roles
export function useAdminApi() {
  const { user } = useAuth();
  const api = useRoleBasedApi();
  
  // Check if user has admin role
  const isAdmin = user?.role === 'admin' || user?.roles?.includes('admin');
  
  return {
    ...api,
    isAdmin,
    // Admin-specific convenience methods
    getUsers: api.getAdminUsers,
    getOperators: api.getAdminOperators,
    getLocations: api.getAdminLocations,
    getServices: api.getAdminServices,
    getBookings: api.getAdminBookings,
  };
}

export function useOperatorApi() {
  const { user } = useAuth();
  const api = useRoleBasedApi();
  
  // Check if user has operator role
  const isOperator = user?.role === 'operator' || user?.roles?.includes('operator');
  
  return {
    ...api,
    isOperator,
    // Operator-specific convenience methods
    getLocations: api.getOperatorLocations,
    getServices: api.getOperatorServices,
    getBookings: api.getOperatorBookings,
    getDashboardData: api.getOperatorDashboardData,
    getProfile: api.getOperatorProfile,
    updateProfile: api.updateOperatorProfile,
  };
}

export function useGuestApi() {
  const { user } = useAuth();
  const api = useRoleBasedApi();
  
  // Check if user has guest role
  const isGuest = user?.role === 'guest' || user?.roles?.includes('guest');
  
  return {
    ...api,
    isGuest,
    // Guest-specific convenience methods
    getBookings: api.getUserBookings,
    getProfile: api.getUserProfile,
    updateProfile: api.updateUserProfile,
    updatePassword: api.updateUserPassword,
    updateAvatar: api.updateUserAvatar,
    getPreferences: api.getUserPreferences,
    updatePreferences: api.updateUserPreferences,
  };
} 